import { divideArrayIntoChunks } from '@pkg/basic'
import {
  and,
  asc,
  countDistinct,
  desc,
  eq,
  gt,
  inArray,
  lt,
  or,
  sql,
} from 'drizzle-orm'
import { z } from 'zod'
import { sqliteMaxHostParameterCount } from '../../constants'
import {
  actionTable,
  assistantProposalActionTable,
  assistantProposalTable,
} from '../../schema'
import type {
  AssistantRuleKey,
  DatabaseWithTransaction,
  TransactionDatabase,
} from '../../types'

/** 提案一覧の安定ソート位置。 */
export type AssistantProposalCursor = {
  readonly primaryActionTimestamp: Date
  readonly primarySourceId: string
  readonly primaryActionOrder: number
  readonly proposalId: string
}

/** rule単位の保留提案件数。 */
export type AssistantProposalGroupSummary = {
  readonly ruleKey: AssistantRuleKey
  readonly proposalCount: number
  readonly targetActionCount: number
}

/** rule別candidate pageの取得条件。 */
export type GetAssistantProposalCandidatePageParams = {
  readonly ruleKey: AssistantRuleKey
  readonly direction: 'next' | 'previous'
  readonly cursor: AssistantProposalCursor | null
  readonly limit: number
}

/** 通常のASC順へ正規化したproposal candidateと関連行。 */
export type AssistantProposalCandidateRecordPage = {
  readonly proposals: readonly (typeof assistantProposalTable.$inferSelect)[]
  readonly proposalActions: readonly (typeof assistantProposalActionTable.$inferSelect)[]
}

/** cursor queryが返す候補と取得方向。 */
export type AssistantProposalCandidatePage = {
  readonly candidates: readonly (typeof assistantProposalTable.$inferSelect)[]
  readonly databaseOrder: 'ascending' | 'descending'
}

/** ID指定で取得した提案とAction関連行。 */
export type AssistantProposalBatchRecords = {
  readonly proposals: readonly (typeof assistantProposalTable.$inferSelect)[]
  readonly proposalActions: readonly (typeof assistantProposalActionTable.$inferSelect)[]
  readonly actions: readonly (typeof actionTable.$inferSelect)[]
}

const cursorSchema = z.object({
  primaryActionTimestamp: z.date(),
  primarySourceId: z.string().min(1),
  primaryActionOrder: z.number().int(),
  proposalId: z.string().min(1),
})

const candidateParamsSchema = z.object({
  ruleKey: z.string().min(1),
  cursor: cursorSchema.nullable(),
  direction: z.enum(['forward', 'backward']),
  limit: z.number().int().positive(),
})

/** proposal rowから外部公開cursorを作成する。 */
export const createAssistantProposalCursor = ({
  proposal,
}: {
  readonly proposal: typeof assistantProposalTable.$inferSelect
}): AssistantProposalCursor => ({
  primaryActionTimestamp: proposal.primaryActionTimestamp,
  primarySourceId: proposal.primarySourceId,
  primaryActionOrder: proposal.primaryActionOrder,
  proposalId: proposal.id,
})

/** cursorより後ろにあることを表す排他的な辞書順条件を作る。 */
const createAfterCondition = ({
  cursor,
}: {
  readonly cursor: AssistantProposalCursor
}) =>
  or(
    gt(
      assistantProposalTable.primaryActionTimestamp,
      cursor.primaryActionTimestamp,
    ),
    and(
      eq(
        assistantProposalTable.primaryActionTimestamp,
        cursor.primaryActionTimestamp,
      ),
      gt(assistantProposalTable.primarySourceId, cursor.primarySourceId),
    ),
    and(
      eq(
        assistantProposalTable.primaryActionTimestamp,
        cursor.primaryActionTimestamp,
      ),
      eq(assistantProposalTable.primarySourceId, cursor.primarySourceId),
      gt(assistantProposalTable.primaryActionOrder, cursor.primaryActionOrder),
    ),
    and(
      eq(
        assistantProposalTable.primaryActionTimestamp,
        cursor.primaryActionTimestamp,
      ),
      eq(assistantProposalTable.primarySourceId, cursor.primarySourceId),
      eq(assistantProposalTable.primaryActionOrder, cursor.primaryActionOrder),
      gt(assistantProposalTable.id, cursor.proposalId),
    ),
  )

/** cursorより前にあることを表す排他的な辞書順条件を作る。 */
const createBeforeCondition = ({
  cursor,
}: {
  readonly cursor: AssistantProposalCursor
}) =>
  or(
    lt(
      assistantProposalTable.primaryActionTimestamp,
      cursor.primaryActionTimestamp,
    ),
    and(
      eq(
        assistantProposalTable.primaryActionTimestamp,
        cursor.primaryActionTimestamp,
      ),
      lt(assistantProposalTable.primarySourceId, cursor.primarySourceId),
    ),
    and(
      eq(
        assistantProposalTable.primaryActionTimestamp,
        cursor.primaryActionTimestamp,
      ),
      eq(assistantProposalTable.primarySourceId, cursor.primarySourceId),
      lt(assistantProposalTable.primaryActionOrder, cursor.primaryActionOrder),
    ),
    and(
      eq(
        assistantProposalTable.primaryActionTimestamp,
        cursor.primaryActionTimestamp,
      ),
      eq(assistantProposalTable.primarySourceId, cursor.primarySourceId),
      eq(assistantProposalTable.primaryActionOrder, cursor.primaryActionOrder),
      lt(assistantProposalTable.id, cursor.proposalId),
    ),
  )

/** proposalが存在するruleだけを本文非取得の集計SQLで返す。 */
export const getAssistantProposalGroupSummariesFromDatabase = async ({
  database,
}: {
  readonly database: TransactionDatabase
}): Promise<readonly AssistantProposalGroupSummary[]> => {
  const rows = await database
    .select({
      ruleKey: assistantProposalTable.ruleKey,
      proposalCount: countDistinct(assistantProposalTable.id),
      targetActionCount: countDistinct(
        sql<string>`printf(
          '%d:%s:%d',
          length(${assistantProposalActionTable.sourceId}),
          ${assistantProposalActionTable.sourceId},
          ${assistantProposalActionTable.actionOrder}
        )`,
      ),
    })
    .from(assistantProposalTable)
    .leftJoin(
      assistantProposalActionTable,
      and(
        eq(assistantProposalActionTable.proposalId, assistantProposalTable.id),
        eq(assistantProposalActionTable.role, 'target'),
      ),
    )
    .groupBy(assistantProposalTable.ruleKey)

  return rows.map((row) => ({
    ruleKey: row.ruleKey,
    proposalCount: Number(row.proposalCount),
    targetActionCount: Number(row.targetActionCount),
  }))
}

/** portfolio databaseからrule別軽量集計を返す。 */
export const getAssistantProposalGroupSummaries = ({
  database: { database },
}: {
  readonly database: DatabaseWithTransaction
}): Promise<readonly AssistantProposalGroupSummary[]> =>
  getAssistantProposalGroupSummariesFromDatabase({ database })

/** rule内を保存済みprimaryキーだけで排他的cursor走査する。 */
export const getAssistantProposalCandidatesFromDatabase = async ({
  database,
  ruleKey,
  cursor,
  direction,
  limit = 50,
}: {
  readonly database: TransactionDatabase
  readonly ruleKey: string
  readonly cursor: AssistantProposalCursor | null
  readonly direction: 'forward' | 'backward'
  readonly limit?: number
}): Promise<AssistantProposalCandidatePage> => {
  const parsed = candidateParamsSchema.parse({
    ruleKey,
    cursor,
    direction,
    limit,
  })
  const cursorCondition =
    parsed.cursor === null
      ? undefined
      : parsed.direction === 'forward'
        ? createAfterCondition({ cursor: parsed.cursor })
        : createBeforeCondition({ cursor: parsed.cursor })
  const where =
    cursorCondition === undefined
      ? eq(assistantProposalTable.ruleKey, parsed.ruleKey)
      : and(eq(assistantProposalTable.ruleKey, parsed.ruleKey), cursorCondition)
  const order =
    parsed.direction === 'forward'
      ? [
          asc(assistantProposalTable.primaryActionTimestamp),
          asc(assistantProposalTable.primarySourceId),
          asc(assistantProposalTable.primaryActionOrder),
          asc(assistantProposalTable.id),
        ]
      : [
          desc(assistantProposalTable.primaryActionTimestamp),
          desc(assistantProposalTable.primarySourceId),
          desc(assistantProposalTable.primaryActionOrder),
          desc(assistantProposalTable.id),
        ]
  const candidates = await database
    .select()
    .from(assistantProposalTable)
    .where(where)
    .orderBy(...order)
    .limit(parsed.limit)

  return {
    candidates,
    databaseOrder: parsed.direction === 'forward' ? 'ascending' : 'descending',
  }
}

/** portfolio databaseからrule内のcandidate pageを返す。 */
export const getAssistantProposalCandidates = ({
  database: { database },
  ruleKey,
  cursor,
  direction,
  limit = 50,
}: {
  readonly database: DatabaseWithTransaction
  readonly ruleKey: string
  readonly cursor: AssistantProposalCursor | null
  readonly direction: 'forward' | 'backward'
  readonly limit?: number
}): Promise<AssistantProposalCandidatePage> =>
  getAssistantProposalCandidatesFromDatabase({
    database,
    ruleKey,
    cursor,
    direction,
    limit,
  })

/**
 * rule内のproposalとrelationだけを排他的cursorで取得する。
 * @param params database、rule、方向、cursor、上限。
 * @returns proposalとdisplayOrder順のrelation。previousも通常のASC順で返す。
 */
export const getAssistantProposalCandidatePageFromDatabase = async ({
  database,
  ruleKey,
  direction,
  cursor,
  limit,
}: GetAssistantProposalCandidatePageParams & {
  readonly database: TransactionDatabase
}): Promise<AssistantProposalCandidateRecordPage> => {
  const candidatePage = await getAssistantProposalCandidatesFromDatabase({
    database,
    ruleKey,
    cursor,
    direction: direction === 'next' ? 'forward' : 'backward',
    limit,
  })
  const proposals =
    direction === 'next'
      ? candidatePage.candidates
      : candidatePage.candidates.toReversed()
  if (proposals.length === 0) {
    return { proposals: [], proposalActions: [] }
  }
  const relationGroups = await Promise.all(
    divideArrayIntoChunks(
      proposals.map((proposal) => proposal.id),
      sqliteMaxHostParameterCount,
    ).map((chunk) =>
      database
        .select()
        .from(assistantProposalActionTable)
        .where(inArray(assistantProposalActionTable.proposalId, [...chunk]))
        .orderBy(
          asc(assistantProposalActionTable.proposalId),
          asc(assistantProposalActionTable.displayOrder),
        ),
    ),
  )
  return {
    proposals,
    proposalActions: relationGroups.flat(),
  }
}

/**
 * portfolio databaseからrule内のproposal candidate pageを取得する。
 * @param params database、rule、方向、cursor、上限。
 * @returns 通常のASC順のproposalとdisplayOrder順のrelation。
 */
export const getAssistantProposalCandidatePage = ({
  database: { database },
  ...params
}: GetAssistantProposalCandidatePageParams & {
  readonly database: DatabaseWithTransaction
}): Promise<AssistantProposalCandidateRecordPage> =>
  getAssistantProposalCandidatePageFromDatabase({ database, ...params })

/** ruleに属するproposal IDを通常一覧と同じ安定順でsnapshotする。 */
export const getAssistantProposalIdsByRuleKeyFromDatabase = async ({
  database,
  ruleKey,
}: {
  readonly database: TransactionDatabase
  readonly ruleKey: string
}): Promise<readonly string[]> => {
  const parsedRuleKey = z.string().min(1).parse(ruleKey)
  const rows = await database
    .select({ id: assistantProposalTable.id })
    .from(assistantProposalTable)
    .where(eq(assistantProposalTable.ruleKey, parsedRuleKey))
    .orderBy(
      asc(assistantProposalTable.primaryActionTimestamp),
      asc(assistantProposalTable.primarySourceId),
      asc(assistantProposalTable.primaryActionOrder),
      asc(assistantProposalTable.id),
    )
  return rows.map((row) => row.id)
}

/** portfolio databaseからrule単位のproposal ID snapshotを返す。 */
export const getAssistantProposalIdsByRuleKey = ({
  database: { database },
  ruleKey,
}: {
  readonly database: DatabaseWithTransaction
  readonly ruleKey: string
}): Promise<readonly string[]> =>
  getAssistantProposalIdsByRuleKeyFromDatabase({ database, ruleKey })

/** ID群に対応するproposal、relation、Actionをhost parameter上限内で取得する。 */
export const getAssistantProposalBatchRecordsFromDatabase = async ({
  database,
  proposalIds,
}: {
  readonly database: TransactionDatabase
  readonly proposalIds: readonly string[]
}): Promise<AssistantProposalBatchRecords> => {
  const ids = [...new Set(z.array(z.string().min(1)).parse(proposalIds))]
  if (ids.length === 0) {
    return { proposals: [], proposalActions: [], actions: [] }
  }
  const chunks = divideArrayIntoChunks(ids, sqliteMaxHostParameterCount)
  const proposalGroups = await Promise.all(
    chunks.map((chunk) =>
      database
        .select()
        .from(assistantProposalTable)
        .where(inArray(assistantProposalTable.id, [...chunk])),
    ),
  )
  const relationGroups = await Promise.all(
    chunks.map((chunk) =>
      database
        .select()
        .from(assistantProposalActionTable)
        .where(inArray(assistantProposalActionTable.proposalId, [...chunk]))
        .orderBy(
          asc(assistantProposalActionTable.proposalId),
          asc(assistantProposalActionTable.displayOrder),
        ),
    ),
  )
  const actionGroups = await Promise.all(
    chunks.map((chunk) =>
      database
        .select({ action: actionTable })
        .from(actionTable)
        .innerJoin(
          assistantProposalActionTable,
          and(
            eq(actionTable.sourceId, assistantProposalActionTable.sourceId),
            eq(actionTable.order, assistantProposalActionTable.actionOrder),
          ),
        )
        .where(inArray(assistantProposalActionTable.proposalId, [...chunk])),
    ),
  )
  const actionById = new Map<string, typeof actionTable.$inferSelect>()
  for (const group of actionGroups) {
    for (const row of group) {
      actionById.set(row.action.id, row.action)
    }
  }
  return {
    proposals: proposalGroups.flat(),
    proposalActions: relationGroups.flat(),
    actions: [...actionById.values()],
  }
}

/** portfolio databaseからID指定のproposal関連行をbatch取得する。 */
export const getAssistantProposalBatchRecords = ({
  database: { database },
  proposalIds,
}: {
  readonly database: DatabaseWithTransaction
  readonly proposalIds: readonly string[]
}): Promise<AssistantProposalBatchRecords> =>
  getAssistantProposalBatchRecordsFromDatabase({ database, proposalIds })
